import { z } from 'zod';
import { AMOUNT_SCALE, fromScaled, toScaled } from '../../money.ts';
import type { LocalizedText, PermissionDefinition } from '../../permissions.ts';

/** Módulo Compras (catálogo beadev-ai/catalogo): permissões, regras, validações e tipos. */

export const PERMISSION_MODULES = {
  purchases: { 'pt-BR': 'Compras', en: 'Purchases' },
} satisfies Record<string, LocalizedText>;

export const PERMISSIONS = [
  {
    code: 'purchases.purchase.read',
    module: 'purchases',
    label: { 'pt-BR': 'Ver compras', en: 'View purchases' },
  },
  {
    code: 'purchases.purchase.manage',
    module: 'purchases',
    label: {
      'pt-BR': 'Criar, alterar, enviar, receber e cancelar pedidos de compra',
      en: 'Create, edit, send, receive and cancel purchase orders',
    },
  },
] as const satisfies readonly PermissionDefinition[];

export const PURCHASE_STATUSES = ['draft', 'ordered', 'received', 'cancelled'] as const;
export type PurchaseStatus = (typeof PURCHASE_STATUSES)[number];

/**
 * Para onde cada situação pode ir. Só o rascunho muda de itens; recebido e cancelado são finais.
 * O banco confere a mesma regra (purchases.check_purchase_change).
 */
export const NEXT_STATUSES: Record<PurchaseStatus, readonly PurchaseStatus[]> = {
  draft: ['ordered', 'cancelled'],
  ordered: ['received', 'cancelled'],
  received: [],
  cancelled: [],
};

export function canMove(from: PurchaseStatus, to: PurchaseStatus): boolean {
  return NEXT_STATUSES[from].includes(to);
}

/**
 * Texto decimal digitado (aceita vírgula: "1,5", "1.234,56") no formato da base ("1.5",
 * "1234.56"). Devolve null se não for um número.
 */
export function normalizeDecimal(value: string): string | null {
  let s = value.trim().replace(/\s/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  return /^\d+(\.\d{1,4})?$/.test(s) ? s : null;
}

/**
 * Valor de um item: quantidade × preço, arredondado em 4 casas para longe do zero, como o
 * `round(quantity * unit_cost, 4)` do banco. Tudo em bigint, nunca em ponto flutuante.
 */
export function lineAmount(quantity: string, unitCost: string): string {
  const factor = 10n ** BigInt(AMOUNT_SCALE);
  const product = toScaled(quantity) * toScaled(unitCost);
  const negative = product < 0n;
  const abs = negative ? -product : product;
  const rounded = (abs + factor / 2n) / factor;
  return fromScaled(negative ? -rounded : rounded);
}

/** A soma dos valores dos itens. */
export function purchaseTotal(items: readonly { quantity: string; unitCost: string }[]): string {
  return fromScaled(
    items.reduce((sum, i) => sum + toScaled(lineAmount(i.quantity, i.unitCost)), 0n),
  );
}

/** Número do pedido para mostrar: 12 → OC-000012. */
export function purchaseLabel(number: number): string {
  return `OC-${String(number).padStart(6, '0')}`;
}

const decimal = (positive: boolean) =>
  z
    .string()
    .max(30)
    .transform((s, ctx) => {
      const n = normalizeDecimal(s);
      if (n === null || toScaled(n) > 999_999_999_999_999n * 10n ** 4n) {
        ctx.addIssue({ code: 'custom', message: 'Número inválido (até 4 casas decimais)' });
        return z.NEVER;
      }
      if (positive && toScaled(n) === 0n) {
        ctx.addIssue({ code: 'custom', message: 'Precisa ser maior que zero' });
        return z.NEVER;
      }
      return n;
    });

export const purchaseItemInput = z.object({
  description: z.string().trim().min(1).max(200),
  quantity: decimal(true),
  unitCost: decimal(false),
});
export type PurchaseItemInput = z.input<typeof purchaseItemInput>;

export const purchaseInput = z.object({
  supplierId: z.uuid(),
  /** Data prevista de chegada (AAAA-MM-DD); vazio = sem data. */
  dueDate: z
    .union([z.literal(''), z.iso.date()])
    .nullable()
    .optional()
    .transform((d) => d || null),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/)
    .default('BRL'),
  notes: z
    .string()
    .trim()
    .max(2000)
    .nullable()
    .optional()
    .transform((s) => s || null),
  items: z.array(purchaseItemInput).min(1).max(100),
});
export type PurchaseInput = z.input<typeof purchaseInput>;

export const purchaseStatusInput = z.object({ status: z.enum(PURCHASE_STATUSES) });

export interface PurchaseItem {
  id: string;
  description: string;
  quantity: string;
  unitCost: string;
  amount: string;
}

/** O pedido como a lista mostra. */
export interface PurchaseSummary {
  id: string;
  number: number;
  supplierId: string;
  /** null se a pessoa não pode ver o fornecedor. */
  supplierName: string | null;
  status: PurchaseStatus;
  dueDate: string | null;
  currency: string;
  total: string;
  itemCount: number;
  createdAt: string;
}

/** O pedido completo, com os itens. */
export interface Purchase extends PurchaseSummary {
  notes: string | null;
  items: PurchaseItem[];
  orderedAt: string | null;
  receivedAt: string | null;
  cancelledAt: string | null;
}
